<script setup lang="ts">
  defineOptions({
    name: 'CPPortalSidebar',
  })

  import type { PropType } from 'vue'
  import { computed } from 'vue'

  import AppSidebar from '@common/shared-ui-layer/components/AppSidebar/AppSidebar.vue'

  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
  import { useCoursesAdminPreview } from '@courses/admin-preview-layer/composables/useCoursesAdminPreview'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPText from '@ui/components/CPText.vue'

  import CPPortalSidebarNav from './CPPortalSidebarNav.vue'

  import type { PortalNavigationGroup, PortalNavigationItem } from '../types/portal-sidebar'

  defineProps({
    portalIcon: {
      type: String as PropType<string | null>,
      default: null,
    },
    portalName: {
      type: String,
      required: true,
    },
    portalSubtitle: {
      type: String as PropType<string | null>,
      default: null,
    },
    navigationGroups: {
      type: Array as PropType<PortalNavigationGroup[]>,
      required: true,
    },
    selectedItem: {
      type: Object as PropType<PortalNavigationItem | undefined>,
      default: undefined,
    },
    isCollapsed: {
      type: Boolean,
      default: false,
    },
    isVisible: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    toggle: []
    itemClick: [item: PortalNavigationItem]
  }>()

  const { getRoutePath } = useCPNavigation()

  // Freeze the sidebar in admin-preview builder mode so the previewing agency can't navigate away.
  const { isActive, builderMode } = useCoursesAdminPreview()
  const isAdminPreviewBuilder = computed(() => Boolean(isActive.value && builderMode.value))
</script>

<template>
  <AppSidebar
    v-if="isVisible"
    class="h-full min-h-0 w-full max-h-full cp-sidebar"
    :class="{ 'pointer-events-none select-none': isAdminPreviewBuilder }"
    :aria-disabled="isAdminPreviewBuilder"
    :inert="isAdminPreviewBuilder"
    :show-toggle="false"
    :is-collapsed="isCollapsed"
    :on-toggle="() => emit('toggle')"
    :show-border="false"
    :show-divider="false"
    :show-footer="false"
  >
    <template #header>
      <div class="cp-portal-sidebar-header group/header" :data-collapsible="isCollapsed ? 'icon' : 'content'">
        <NuxtLink
          :to="getRoutePath({ name: 'clientportal-dashboard' })"
          class="h-12 px-2 py-2 flex w-full min-w-0 items-center gap-2 no-underline text-inherit outline-none overflow-hidden rounded-md hover:no-underline focus:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary hover:bg-sidebar-hover ease-linear transition-[padding,gap] duration-200 group-data-[collapsible=icon]/header:justify-center group-data-[collapsible=icon]/header:gap-0 group-data-[collapsible=icon]/header:p-0"
        >
          <CPAvatar :src="portalIcon || ''" :alt="portalName" :title="portalName" shape="square" size="sm" class="flex-shrink-0" />
          <div
            class="grid min-w-0 flex-1 overflow-hidden text-left transition-[width,opacity] duration-200 group-data-[collapsible=icon]/header:pointer-events-none group-data-[collapsible=icon]/header:w-0 group-data-[collapsible=icon]/header:opacity-0"
          >
            <CPText size="lg" weight="medium" htmlTag="span" class="text-on-sidebar truncate !leading-tight">
              {{ portalName }}
            </CPText>
            <CPText v-if="portalSubtitle" size="sm" htmlTag="span" class="text-on-sidebar truncate !leading-tight">
              {{ portalSubtitle }}
            </CPText>
          </div>
        </NuxtLink>
      </div>
    </template>

    <template #content>
      <CPPortalSidebarNav
        :portal-name="portalName"
        :navigation-groups="navigationGroups"
        :selected-item="selectedItem"
        :is-collapsed="isCollapsed"
        @item-click="emit('itemClick', $event)"
      />
    </template>
  </AppSidebar>
</template>

<style scoped>
  :deep(.app-sidebar-header) {
    height: var(--cp-topbar-height, 52px);
    padding-bottom: 0;
  }

  :deep(.app-sidebar-content) {
    padding-block: 0.75rem;
  }

  .cp-portal-sidebar-header {
    min-height: var(--cp-topbar-height, 52px);
    width: 100%;
    padding-block: 4px;
    display: flex;
    align-items: center;
  }

  :deep(.cp-sidebar) {
    grid-column: 1;
    grid-row: 1;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    width: var(--cp-sidebar-width);
    overflow: hidden;
    transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 50;
    position: relative;
  }
</style>
